import { config } from "../Environment";

/**
 * When an engagement runs, the diagram comes from fiPrescriptionHistory (snapshot at process
 * creation). Authors edit the live Prescription.diagramJSON (autosave), so component settings
 * can be newer than the snapshot. Merge matching data_event/data_event_analysis blocks
 * (same activity node id + component id) so runners see current UI config without changing
 * flow structure from the snapshot.
 */
export function mergeDataEventAnalysisFromLivePrescription(
  diagramFromProcessSnapshot,
  livePrescriptionDiagramJSON,
) {
  if (
    !diagramFromProcessSnapshot ||
    typeof diagramFromProcessSnapshot !== "object" ||
    !diagramFromProcessSnapshot.data ||
    livePrescriptionDiagramJSON == null ||
    livePrescriptionDiagramJSON === ""
  ) {
    return diagramFromProcessSnapshot;
  }

  let current;
  try {
    current =
      typeof livePrescriptionDiagramJSON === "string"
        ? JSON.parse(livePrescriptionDiagramJSON)
        : livePrescriptionDiagramJSON;
  } catch {
    return diagramFromProcessSnapshot;
  }

  if (!current || typeof current !== "object" || !current.data) {
    return diagramFromProcessSnapshot;
  }

  let merged;
  try {
    merged =
      typeof structuredClone === "function"
        ? structuredClone(diagramFromProcessSnapshot)
        : JSON.parse(JSON.stringify(diagramFromProcessSnapshot));
  } catch {
    return diagramFromProcessSnapshot;
  }

  for (const nodeId of Object.keys(merged.data)) {
    const histPane = merged.data[nodeId];
    const currPane = current.data[nodeId];
    if (!Array.isArray(histPane?.custom) || !Array.isArray(currPane?.custom)) {
      continue;
    }

    const currByCompId = new Map();
    for (const c of currPane.custom) {
      if (c && c.id != null) currByCompId.set(String(c.id), c);
    }

    histPane.custom = histPane.custom.map((hComp) => {
      if (
        (hComp?.type !== "data_event_analysis" && hComp?.type !== "data_event") ||
        hComp?.id == null
      ) {
        return hComp;
      }
      const cur = currByCompId.get(String(hComp.id));
      if (
        !cur ||
        (cur.type !== "data_event_analysis" && cur.type !== "data_event")
      )
        return hComp;
      if (cur.type !== hComp.type) return hComp;
      const hContent =
        hComp.content && typeof hComp.content === "object" ? hComp.content : {};
      const cContent =
        cur.content && typeof cur.content === "object" ? cur.content : {};
      return {
        ...hComp,
        content: { ...hContent, ...cContent },
      };
    });
  }

  return merged;
}

/**
 * Fetch live prescription and merge data_event_analysis content into dia.
 */
export async function applyLivePrescriptionDiagramMerge(
  dia,
  prescriptionId,
  retryFetch,
) {
  const id =
    prescriptionId && typeof prescriptionId === "object" && prescriptionId._id
      ? prescriptionId._id
      : prescriptionId;
  if (!id || !dia?.data) return dia;
  try {
    const res = await retryFetch(`${config.API_BASE}/api/prescriptions/${id}`);
    if (!res.ok) return dia;
    const pres = await res.json();
    if (pres.diagramJSON) {
      return mergeDataEventAnalysisFromLivePrescription(dia, pres.diagramJSON);
    }
  } catch (e) {
    console.warn(
      "[applyLivePrescriptionDiagramMerge] prescription fetch failed",
      e,
    );
  }
  return dia;
}
